import React, { useEffect, useState } from 'react';
import { Card } from './ui/Card';
import { Badge } from './ui/Badge';
import { Button } from './ui/Button';
import { HeartIcon, MapPinIcon, ClockIcon, BellIcon, TrendingUpIcon, StarIcon, PlayCircleIcon, CalendarDaysIcon, NetworkIcon, SparklesIcon, SettingsIcon } from 'lucide-react';
import { useEvent } from '../contexts/EventContext';
import { useLanguage } from '../contexts/LanguageContext';
import { useQuickLinks } from '../contexts/QuickLinksContext';
import { useAlerts } from '../contexts/AlertsContext';
import { QuickLinksCustomizer } from './QuickLinksCustomizer';
interface FeedPost {
  id: string;
  type: 'update' | 'promo' | 'featured' | 'image-ad' | 'announcement' | 'video';
  title: string;
  content?: string;
  timestamp: string;
  image?: string;
  videoUrl?: string;
  sponsor?: string;
  sessionTime?: string;
  location?: string;
  cta?: {
    label: string;
    action: string;
  };
}
interface HeroSlide {
  id: string;
  type: 'welcome' | 'ad';
  title?: string;
  subtitle?: string;
  imageUrl?: string;
  backgroundColor?: string;
}
interface HomePageProps {
  onNavigate?: (page: string) => void;
}
export function HomePage({
  onNavigate
}: HomePageProps) {
  const {
    currentEvent
  } = useEvent();
  const {
    t,
    isRTL
  } = useLanguage();
  const {
    quickLinks,
    resetToDefaults
  } = useQuickLinks();
  const {
    unreadCount
  } = useAlerts();
  const [currentSlide, setCurrentSlide] = useState(0);
  const [showCustomizer, setShowCustomizer] = useState(false);
  // Mock unread notifications count (in real app, this would come from a context/API)
  const [unreadNotifications, setUnreadNotifications] = useState(5);
  // Initialize quick links with event-specific defaults on first load
  useEffect(() => {
    const hasInitialized = localStorage.getItem('quickLinksInitialized');
    if (!hasInitialized) {
      resetToDefaults(currentEvent.id);
      localStorage.setItem('quickLinksInitialized', 'true');
    }
  }, []);
  // Event-specific hero slides
  const getHeroSlides = (): HeroSlide[] => {
    switch (currentEvent.id) {
      case 'muscle':
        return [{
          id: '1',
          type: 'welcome',
          title: 'Welcome Back!',
          subtitle: "Explore today's highlights",
          imageUrl: 'https://uploadthingy.s3.us-west-1.amazonaws.com/8LEo11vDoK7mzhcuPwb6uF/DMS__Hero1.jpg'
        }, {
          id: '2',
          type: 'ad',
          imageUrl: 'https://uploadthingy.s3.us-west-1.amazonaws.com/mWWt3WDR6dJ5GmQBwoR7CZ/DMS__C5.jpg'
        }, {
          id: '3',
          type: 'ad',
          imageUrl: 'https://uploadthingy.s3.us-west-1.amazonaws.com/8N1RyzSrcNRzQrjeoRrFnw/DMS__Basix.jpg'
        }];
      case 'industry':
        return [{
          id: '1',
          type: 'welcome',
          title: 'Welcome Back!',
          subtitle: "Explore today's highlights",
          imageUrl: 'https://uploadthingy.s3.us-west-1.amazonaws.com/w8ABgWKWAQuuo1wEsZvFJL/DAI__Hero1.jpg'
        }];
      case 'active':
        return [{
          id: '1',
          type: 'welcome',
          title: 'Welcome Back!',
          subtitle: "Explore today's highlights",
          imageUrl: 'https://uploadthingy.s3.us-west-1.amazonaws.com/oofBZ7B91TGYKKgnhsR5Bq/DAS__Hero1.jpg'
        }];
      default:
        return [{
          id: '1',
          type: 'welcome',
          title: 'Welcome Back!',
          subtitle: "Explore today's highlights"
        }];
    }
  };
  const heroSlides = getHeroSlides();
  // Reset carousel and scroll to top when event changes
  useEffect(() => {
    setCurrentSlide(0);
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  }, [currentEvent.id]);
  // Auto-advance slides every 3 seconds
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentSlide(prev => (prev + 1) % heroSlides.length);
    }, 3000);
    return () => clearInterval(interval);
  }, [heroSlides.length]);
  // Event-specific feed posts
  const getFeedPosts = (): FeedPost[] => {
    switch (currentEvent.id) {
      case 'muscle':
        return [{
          id: '1',
          type: 'announcement',
          title: 'Starting in 1 Hour: Dubai Muscle Classic Finals',
          content: "The Middle East's biggest bodybuilding competition is about to begin! Over AED70,000 in prize money and 17 PRO CARDS up for grabs.",
          timestamp: '1 hour',
          sessionTime: '2:00 PM - 5:00 PM',
          location: 'Main Stage',
          cta: {
            label: 'Get Directions',
            action: 'navigate'
          }
        }, {
          id: '2',
          type: 'announcement',
          title: 'CBUM Meet & Greet',
          content: 'First 100 VIP ticket holders get exclusive access to meet Chris Bumstead!',
          timestamp: 'Saturday 25th',
          sessionTime: 'Saturday 25th',
          location: 'Eat Routes Booth #B13A',
          image: 'https://uploadthingy.s3.us-west-1.amazonaws.com/8YbKAbPR5ZGcQU6Aud4iXV/DMS__CBUM.jpg',
          cta: {
            label: 'Get Directions',
            action: 'navigate'
          }
        }, {
          id: '3',
          type: 'video',
          title: "Day 1 Highlights: Yesterday's Best Moments",
          content: 'Watch the official recap featuring incredible physiques, backstage moments, and athlete interviews from the opening day.',
          timestamp: '3 hours ago',
          videoUrl: 'https://uploadthingy.s3.us-west-1.amazonaws.com/8LEo11vDoK7mzhcuPwb6uF/DMS__Hero1.jpg',
          cta: {
            label: 'Watch Now',
            action: 'play'
          }
        }, {
          id: '4',
          type: 'image-ad',
          title: '',
          image: 'https://uploadthingy.s3.us-west-1.amazonaws.com/8N1RyzSrcNRzQrjeoRrFnw/DMS__Basix.jpg',
          timestamp: '4 hours ago',
          sponsor: 'Platinum Sponsor'
        }, {
          id: '5',
          type: 'announcement',
          title: 'Dubai Muscle Talks: Q&A with Mr. Olympia Champions',
          content: 'Join us for an exclusive session with bodybuilding legends. Learn their training secrets, nutrition strategies, and mindset techniques.',
          timestamp: '2 hours',
          sessionTime: '4:00 PM - 5:30 PM',
          location: 'Talk Stage',
          cta: {
            label: 'Reserve Seat',
            action: 'remind'
          }
        }, {
          id: '6',
          type: 'image-ad',
          title: '',
          image: 'https://uploadthingy.s3.us-west-1.amazonaws.com/mWWt3WDR6dJ5GmQBwoR7CZ/DMS__C5.jpg',
          timestamp: '5 hours ago',
          sponsor: 'Gold Sponsor'
        }, {
          id: '7',
          type: 'featured',
          title: "FMG International: Women's Bodybuilding Competition",
          content: "Witness fitness, muscle and glamour on an extraordinary stage. The region's top female athletes compete for glory.",
          timestamp: '6 hours ago',
          sponsor: 'Featured Competition',
          cta: {
            label: 'View Schedule',
            action: 'navigate'
          }
        }, {
          id: '8',
          type: 'image-ad',
          title: '',
          image: 'https://uploadthingy.s3.us-west-1.amazonaws.com/mcRscSUw9fa8MRc1R7v7t8/DMS__LovinDubai.jpg',
          timestamp: '7 hours ago',
          sponsor: 'Media Partner'
        }];
      case 'industry':
        return [{
          id: '1',
          type: 'announcement',
          title: 'Starting Soon: ME Fitness Business Summit Keynote',
          content: 'Industry giants come together to elevate their leadership game. Join us for the opening keynote on building thriving fitness businesses.',
          timestamp: '30 minutes',
          sessionTime: '10:00 AM - 11:30 AM',
          location: 'Summit Hall A',
          cta: {
            label: 'Join Session',
            action: 'navigate'
          }
        }, {
          id: '2',
          type: 'image-ad',
          title: '',
          image: 'https://uploadthingy.s3.us-west-1.amazonaws.com/rj5GoLWAQMRSa95qn3zjME/DAI__Enhance.jpg',
          timestamp: '2 hours ago',
          sponsor: 'Platinum Sponsor'
        }, {
          id: '3',
          type: 'video',
          title: 'Day 1 Summit Highlights',
          content: "Watch key insights from yesterday's sessions featuring industry leaders discussing the future of wellness and fitness business innovation.",
          timestamp: '3 hours ago',
          videoUrl: 'https://uploadthingy.s3.us-west-1.amazonaws.com/w8ABgWKWAQuuo1wEsZvFJL/DAI__Hero1.jpg',
          cta: {
            label: 'Watch Now',
            action: 'play'
          }
        }, {
          id: '4',
          type: 'announcement',
          title: 'XPRO Summit: Advanced Training Methodologies',
          content: 'Comprehensive 3-day programme empowering fitness professionals with cutting-edge knowledge and hands-on training from world-class experts.',
          timestamp: '1 hour',
          sessionTime: '2:00 PM - 4:00 PM',
          location: 'Training Hall B',
          cta: {
            label: 'Register Now',
            action: 'remind'
          }
        }, {
          id: '5',
          type: 'image-ad',
          title: '',
          image: 'https://uploadthingy.s3.us-west-1.amazonaws.com/92t9xMoYTtKVYzktH3JH5Z/DAI__YourPT.jpg',
          timestamp: '4 hours ago',
          sponsor: 'Gold Sponsor'
        }, {
          id: '6',
          type: 'featured',
          title: 'MENA Roundtable: Reimagining Urban Wellness',
          content: 'Eight industry leaders discuss fitness as a force for healthier communities. Join the conversation on shaping active, well-being-focused lifestyles.',
          timestamp: '2 hours',
          sessionTime: '3:30 PM - 5:00 PM',
          location: 'Roundtable Room',
          sponsor: 'Featured Session',
          cta: {
            label: 'Reserve Seat',
            action: 'navigate'
          }
        }, {
          id: '7',
          type: 'image-ad',
          title: '',
          image: 'https://uploadthingy.s3.us-west-1.amazonaws.com/eVdTDdzQTkUTbWnZAghwf4/DAI__AnytimeFitness.jpg',
          timestamp: '5 hours ago',
          sponsor: 'Platinum Sponsor'
        }, {
          id: '8',
          type: 'announcement',
          title: 'Future Wellness Summit: Cutting-Edge Research',
          content: 'A pivotal platform bringing together visionary ideas and transformative practices. Explore the harmonious blend of ancient and modern wellness approaches.',
          timestamp: '3 hours',
          sessionTime: '11:00 AM - 12:30 PM',
          location: 'Wellness Hall',
          cta: {
            label: 'View Agenda',
            action: 'navigate'
          }
        }];
      case 'active':
        return [{
          id: '1',
          type: 'announcement',
          title: 'James Smith Business Mentorship Live Dubai',
          content: 'Join James Smith Live at Dubai Active for a full day of business mentorship and networking. Learn how to grow your online business, master marketing, and automate sales, then connect with James at the Neutonic Meet & Greet. Includes 3-day access to Dubai Active Show.',
          timestamp: 'October 26th',
          sessionTime: 'October 26th, 2025',
          location: 'Business Mentorship Hall',
          image: 'https://uploadthingy.s3.us-west-1.amazonaws.com/9NuyGHK8bpuqwrF4tkyAby/DAS__JamesSmithLive.jpg',
          cta: {
            label: 'Book Now',
            action: 'navigate'
          }
        }, {
          id: '2',
          type: 'announcement',
          title: 'Starting in 30 Minutes: HYROX 365 Challenge Finals',
          content: 'The ultimate fitness race is about to begin! Watch elite athletes compete across 8 stations in this high-intensity endurance challenge.',
          timestamp: '30 minutes',
          sessionTime: '2:00 PM - 4:00 PM',
          location: 'Active Arena',
          cta: {
            label: 'Get Directions',
            action: 'navigate'
          }
        }, {
          id: '3',
          type: 'image-ad',
          title: '',
          image: 'https://uploadthingy.s3.us-west-1.amazonaws.com/ciEjLQnWceZUM9v3GpDRGk/DAS__Hyrox.jpg',
          timestamp: '2 hours ago',
          sponsor: 'Featured Event'
        }, {
          id: '4',
          type: 'video',
          title: "Day 1 Highlights: Yesterday's Best Moments",
          content: 'Watch the official recap from our organizing team featuring top performances, energetic classes, and inspiring fitness moments from the opening day.',
          timestamp: '3 hours ago',
          videoUrl: 'https://uploadthingy.s3.us-west-1.amazonaws.com/oofBZ7B91TGYKKgnhsR5Bq/DAS__Hero1.jpg',
          cta: {
            label: 'Watch Now',
            action: 'play'
          }
        }, {
          id: '5',
          type: 'announcement',
          title: 'Zumba Masterclass with International Instructors',
          content: 'Join the party! High-energy dance fitness session led by world-renowned Zumba instructors. All levels welcome for this explosive workout.',
          timestamp: '1 hour',
          sessionTime: '10:00 AM - 11:00 AM',
          location: 'Main Fitness Stage',
          cta: {
            label: 'Book Now',
            action: 'remind'
          }
        }, {
          id: '6',
          type: 'image-ad',
          title: '',
          image: 'https://uploadthingy.s3.us-west-1.amazonaws.com/cYg6cPUL7wa6b1EtzNXibq/DAS__Seissense.jpg',
          timestamp: '4 hours ago',
          sponsor: 'Gold Sponsor'
        }, {
          id: '7',
          type: 'featured',
          title: 'Dubai Active Talks: Wellness & Mental Health',
          content: 'Inspiring speakers discuss the connection between physical fitness and mental wellbeing. Learn practical strategies for a balanced, active lifestyle.',
          timestamp: '2 hours',
          sessionTime: '3:00 PM - 4:30 PM',
          location: 'Talk Stage',
          sponsor: 'Featured Session',
          cta: {
            label: 'Reserve Seat',
            action: 'navigate'
          }
        }, {
          id: '8',
          type: 'image-ad',
          title: '',
          image: 'https://uploadthingy.s3.us-west-1.amazonaws.com/mdoWPmEYApV956zrHDnchq/DAS__Sweat.jpg',
          timestamp: '5 hours ago',
          sponsor: 'Platinum Sponsor'
        }, {
          id: '9',
          type: 'announcement',
          title: 'Yoga & Pilates Studio: Morning Flow Session',
          content: 'Start your day with mindful movement. Expert-led yoga and pilates classes designed to energize your body and calm your mind.',
          timestamp: '3 hours',
          sessionTime: '8:00 AM - 9:00 AM',
          location: 'Yoga & Pilates Studio',
          cta: {
            label: 'Join Class',
            action: 'navigate'
          }
        }];
      default:
        return [];
    }
  };
  // Update posts when event changes
  const [posts, setPosts] = useState<FeedPost[]>(getFeedPosts());
  useEffect(() => {
    setPosts(getFeedPosts());
  }, [currentEvent.id]);
  const getTextColor = () => {
    // Always use white for hero text
    return 'white';
  };
  const getOutlineColor = () => {
    if (currentEvent.id === 'active') {
      return '#17313a';
    }
    return currentEvent.colors.primary;
  };
  const currentHeroSlide = heroSlides[currentSlide];
  return <div className="pb-24" data-id="element-3541">
      {/* Hero Section - Auto-scrolling carousel (1:1 ratio) */}
      <div className="relative overflow-hidden" style={{
      paddingBottom: '100%' // 1:1 aspect ratio
    }} data-id="element-3542">
        {/* Slides */}
        <div className="absolute inset-0 flex transition-transform duration-500 ease-in-out" style={{
        transform: isRTL ? `translateX(${currentSlide * 100}%)` : `translateX(-${currentSlide * 100}%)`
      }} data-id="element-3543">
          {heroSlides.map((slide, index) => <div key={slide.id} className="w-full h-full flex-shrink-0 relative" style={{
          backgroundColor: slide.type === 'welcome' && !slide.imageUrl ? currentEvent.colors.primary : '#000'
        }} data-id="element-3544">
              {slide.type === 'welcome' ? <>
                  {slide.imageUrl && <img src={slide.imageUrl} alt="Welcome" className="w-full h-full object-cover" data-id="element-3545" />}
                  <div className="absolute inset-0 flex items-end p-6" style={{
              background: slide.imageUrl ? 'linear-gradient(to top, rgba(0,0,0,0.7), transparent)' : 'none',
              color: 'white'
            }} data-id="element-3546">
                    <div data-id="element-3547">
                      <h2 className="text-3xl font-bold mb-2 font-gotham" data-id="element-3548">
                        {t('home.welcomeBack')}
                      </h2>
                      <p className="opacity-90 text-lg font-gotham" data-id="element-3549">
                        {t('home.exploreHighlights')}
                      </p>
                    </div>
                  </div>
                </> : <img src={slide.imageUrl} alt={`Advertisement ${index}`} className="w-full h-full object-cover" data-id="element-3550" />}
            </div>)}
        </div>

        {/* Slide Indicators */}
        {heroSlides.length > 1 && <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 flex gap-2 z-10" data-id="element-3551">
            {heroSlides.map((_, index) => <button key={index} onClick={() => setCurrentSlide(index)} className="w-2 h-2 rounded-full transition-all" style={{
          backgroundColor: currentSlide === index ? 'white' : 'rgba(255, 255, 255, 0.3)',
          width: currentSlide === index ? '24px' : '8px'
        }} data-id="element-3552" />)}
          </div>}
      </div>

      {/* Quick Actions - Now using customizable quick links */}
      <div className="px-4 mb-6 mt-8 relative z-10" data-id="element-3553">
        <div className="flex items-center justify-between mb-3" data-id="element-3554">
          <h3 className="text-sm font-bold text-gray-900 font-gotham" data-id="element-3555">
            Quick Links
          </h3>
          <button onClick={() => setShowCustomizer(true)} className="flex items-center gap-1 text-xs font-medium font-gotham hover:opacity-70 transition-opacity" style={{
          color: currentEvent.id === 'active' ? currentEvent.colors.secondary : currentEvent.colors.primary
        }} data-id="element-3556">
            <SettingsIcon className="w-3 h-3" data-id="element-3557" />
            Customize
          </button>
        </div>

        <div className="grid grid-cols-4 gap-2" data-id="element-3558">
          {quickLinks.map(link => {
          const Icon = link.icon;
          const isNotifications = link.id === 'notifications';
          const showBadge = isNotifications && unreadCount > 0;
          return <button key={link.id} onClick={() => onNavigate?.(link.page)} className="rounded-xl shadow-lg p-3 flex flex-col items-center gap-2 hover:shadow-xl transition-all active:scale-95 bg-white relative" style={{
            borderTop: `4px solid ${currentEvent.colors.accent}`
          }} data-id="element-3559">
                <div className="w-12 h-12 rounded-full flex items-center justify-center relative" style={{
              backgroundColor: currentEvent.colors.primary,
              color: currentEvent.id === 'active' ? '#17313a' : 'white'
            }} data-id="element-3560">
                  <Icon className="w-6 h-6" data-id="element-3561" />
                  {showBadge && <div className="absolute -top-1 -right-1 w-5 h-5 rounded-full flex items-center justify-center text-white text-[10px] font-bold font-gotham" style={{
                backgroundColor: '#ef4444'
              }} data-id="element-3562">
                      {unreadCount > 9 ? '9+' : unreadCount}
                    </div>}
                </div>
                <span className="text-[10px] font-semibold font-gotham text-center leading-tight" style={{
              color: currentEvent.id === 'active' ? currentEvent.colors.secondary : currentEvent.colors.primary
            }} data-id="element-3563">
                  {t(link.labelKey)}
                </span>
              </button>;
        })}
        </div>
      </div>

      {/* Customizer Modal */}
      <QuickLinksCustomizer isOpen={showCustomizer} onClose={() => setShowCustomizer(false)} data-id="element-3564" />

      {/* Sponsor Banner */}
      <div className="px-4 mb-6" data-id="element-3565">
        <Card padding="md" className="bg-gradient-to-r from-blue-50 to-purple-50 border-none" data-id="element-3566">
          <div className="flex items-center justify-between" data-id="element-3567">
            <div data-id="element-3568">
              <p className="text-xs text-gray-500 font-gotham mb-1" data-id="element-3569">
                {t('home.sponsored')}
              </p>
              <h3 className="font-bold text-gray-900 font-gotham" data-id="element-3570">
                Premium Fitness Gear
              </h3>
              <p className="text-sm text-gray-600 font-gotham" data-id="element-3571">
                Exclusive 30% off for attendees
              </p>
            </div>
            <Button size="sm" data-id="element-3572">{t('home.shopNow')}</Button>
          </div>
        </Card>
      </div>

      {/* Feed Header */}
      <div className="px-4 mb-4" data-id="element-3573">
        <h3 className="text-lg font-bold text-gray-900 font-gotham" data-id="element-3574">
          {t('home.latestUpdates')}
        </h3>
      </div>

      {/* Feed Posts */}
      <div className="px-4 space-y-4" data-id="element-3575">
        {posts.map(post => {
        // Image Ad Post (1:1 ratio)
        if (post.type === 'image-ad') {
          return <div key={post.id} className="relative rounded-xl overflow-hidden shadow-lg" style={{
            paddingBottom: '100%'
          }} data-id="element-3576">
                <img src={post.image} alt="Sponsor Advertisement" className="absolute inset-0 w-full h-full object-cover" data-id="element-3577" />
                {post.sponsor && <div className={`absolute top-3 ${isRTL ? 'left-3' : 'right-3'}`} data-id="element-3578">
                    <Badge variant="default" size="sm" data-id="element-3579">
                      {post.sponsor}
                    </Badge>
                  </div>}
              </div>;
        }
        // Video Post (1:1 ratio)
        if (post.type === 'video') {
          return <Card key={post.id} padding="none" data-id="element-3580">
                <div className="relative" style={{
              paddingBottom: '100%'
            }} data-id="element-3581">
                  <img src={post.videoUrl} alt={post.title} className="absolute inset-0 w-full h-full object-cover" data-id="element-3582" />
                  <div className="absolute inset-0 bg-black bg-opacity-30 flex items-center justify-center" data-id="element-3583">
                    <div className="w-16 h-16 rounded-full bg-white bg-opacity-90 flex items-center justify-center" data-id="element-3584">
                      <PlayCircleIcon className="w-10 h-10 text-gray-900" data-id="element-3585" />
                    </div>
                  </div>
                  <div className={`absolute top-3 ${isRTL ? 'right-3' : 'left-3'}`} data-id="element-3586">
                    <Badge variant="default" size="sm" data-id="element-3587">
                      {t('home.videoRecap')}
                    </Badge>
                  </div>
                </div>
                <div className="p-4" data-id="element-3588">
                  <h4 className="font-bold text-gray-900 mb-1 font-gotham" data-id="element-3589">
                    {post.title}
                  </h4>
                  <p className="text-sm text-gray-600 font-gotham mb-2" data-id="element-3590">
                    {post.content}
                  </p>
                  <p className="text-xs text-gray-400 font-gotham mb-3" data-id="element-3591">
                    {post.timestamp}
                  </p>
                  {post.cta && <Button variant="primary" size="sm" fullWidth data-id="element-3592">
                      {post.cta.label}
                    </Button>}
                </div>
              </Card>;
        }
        // Announcement Post (1:1 ratio for images)
        if (post.type === 'announcement') {
          return <Card key={post.id} padding="none" className="overflow-hidden" data-id="element-3593">
                {/* Image for announcements with images (1:1 ratio) */}
                {post.image && <div className="relative" style={{
              paddingBottom: '100%'
            }} data-id="element-3594">
                    <img src={post.image} alt={post.title} className="absolute inset-0 w-full h-full object-cover" data-id="element-3595" />
                  </div>}

                <div className="p-4" data-id="element-3596">
                  <div className="flex items-start justify-between mb-3" data-id="element-3597">
                    <div className="flex-1" data-id="element-3598">
                      <div className="flex items-center gap-2 mb-2" data-id="element-3599">
                        <Badge variant="warning" size="sm" data-id="element-3600">
                          <StarIcon className={`w-3 h-3 ${isRTL ? 'ml-1' : 'mr-1'} inline`} data-id="element-3601" />
                          {t('home.featuredEvent')}
                        </Badge>
                      </div>
                      <h4 className="font-bold text-gray-900 mb-1 font-gotham" data-id="element-3602">
                        {post.title}
                      </h4>
                      {post.content && <p className="text-sm text-gray-600 font-gotham mb-2" data-id="element-3603">
                          {post.content}
                        </p>}
                      <div className="flex items-center gap-3 text-sm text-gray-500 font-gotham mb-1" data-id="element-3604">
                        <span className="flex items-center gap-1" data-id="element-3605">
                          <ClockIcon className="w-4 h-4" data-id="element-3606" />
                          {post.sessionTime}
                        </span>
                        {post.location && <span className="flex items-center gap-1" data-id="element-3607">
                            <MapPinIcon className="w-4 h-4" data-id="element-3608" />
                            {post.location}
                          </span>}
                      </div>
                      <p className="text-xs text-gray-400 font-gotham" data-id="element-3609">
                        {post.timestamp}
                      </p>
                    </div>
                  </div>

                  {post.cta && <div className="flex gap-2 mt-3" data-id="element-3610">
                      <Button variant="primary" size="sm" fullWidth data-id="element-3611">
                        {post.cta.label}
                      </Button>
                    </div>}
                </div>
              </Card>;
        }
        // Standard Post (featured, update, promo)
        return <Card key={post.id} padding="md" data-id="element-3612">
              <div className="flex items-start justify-between mb-3" data-id="element-3613">
                <div className="flex-1" data-id="element-3614">
                  <div className="flex items-center gap-2 mb-2" data-id="element-3615">
                    {post.type === 'featured' && <Badge variant="warning" size="sm" data-id="element-3616">
                        <StarIcon className={`w-3 h-3 ${isRTL ? 'ml-1' : 'mr-1'} inline`} data-id="element-3617" />
                        Featured
                      </Badge>}
                    {post.sponsor && <Badge variant="default" size="sm" data-id="element-3618">
                        {post.sponsor}
                      </Badge>}
                  </div>
                  <h4 className="font-bold text-gray-900 mb-1 font-gotham" data-id="element-3619">
                    {post.title}
                  </h4>
                  <p className="text-sm text-gray-600 font-gotham mb-2" data-id="element-3620">
                    {post.content}
                  </p>
                  <p className="text-xs text-gray-400 font-gotham" data-id="element-3621">
                    {post.timestamp}
                  </p>
                </div>
              </div>

              {post.cta && <div className="flex gap-2 mt-3" data-id="element-3622">
                  <Button variant="primary" size="sm" fullWidth data-id="element-3623">
                    {post.cta.label}
                  </Button>
                </div>}
            </Card>;
      })}
      </div>

      {/* Trending Section */}
      <div className="px-4 mt-8" data-id="element-3624">
        <div className="flex items-center gap-2 mb-4" data-id="element-3625">
          <TrendingUpIcon className="w-5 h-5" style={{
          color: currentEvent.colors.primary
        }} data-id="element-3626" />
          <h3 className="text-lg font-bold text-gray-900 font-gotham" data-id="element-3627">
            {t('home.trendingNow')}
          </h3>
        </div>

        <div className="flex gap-3 overflow-x-auto pb-2 -mx-4 px-4" data-id="element-3628">
          {currentEvent.id === 'muscle' ? ['Dubai Muscle Classic', 'CBUM Meet & Greet', 'FMG International', 'Muscle Talks'].map((item, index) => <button key={index} className="flex-shrink-0 px-4 py-2 rounded-full border-2 font-medium text-sm font-gotham hover:shadow-md transition-all" style={{
          borderColor: getOutlineColor(),
          color: getOutlineColor()
        }} data-id="element-3629">
                  {item}
                </button>) : currentEvent.id === 'industry' ? ['ME Fitness Summit', 'XPRO Summit', 'MENA Roundtable', 'Future Wellness'].map((item, index) => <button key={index} className="flex-shrink-0 px-4 py-2 rounded-full border-2 font-medium text-sm font-gotham hover:shadow-md transition-all" style={{
          borderColor: getOutlineColor(),
          color: getOutlineColor()
        }} data-id="element-3630">
                    {item}
                  </button>) : ['HYROX 365', 'Zumba Classes', 'Yoga & Pilates', 'Active Talks'].map((item, index) => <button key={index} className="flex-shrink-0 px-4 py-2 rounded-full border-2 font-medium text-sm font-gotham hover:shadow-md transition-all" style={{
          borderColor: getOutlineColor(),
          color: getOutlineColor()
        }} data-id="element-3631">
                    {item}
                  </button>)}
        </div>
      </div>
    </div>;
}